import Component from "@glimmer/component";
import { LinkTo } from "@ember/routing";
import { on } from "@ember/modifier";
import { restartableTask, timeout } from "ember-concurrency";
import { not } from "ember-truth-helpers";
import { t } from "ember-intl";
import ENV from "glesys-controlpanel/config/environment";
import Box from "glesys-controlpanel/components/box";
import BoxEmptyState from "glesys-controlpanel/components/box/empty-state";
import Table from "glesys-controlpanel/components/table";
import TableTh from "glesys-controlpanel/components/table/th";
import TableTd from "glesys-controlpanel/components/table/td";
import Dropdown from "glesys-controlpanel/components/dropdown";
import DropdownItem from "glesys-controlpanel/components/dropdown/item";
import GButtonCreate from "glesys-controlpanel/components/g-button/create";
import IconsMoreVertical from "glesys-controlpanel/components/icons/more-vertical";
import IconsLoadingSpinner from "glesys-controlpanel/components/icons/loading-spinner";
import BadgeSuccess from "glesys-controlpanel/components/badge/success";
import canManage from "glesys-controlpanel/helpers/can-manage";
import modal from "glesys-controlpanel/helpers/modal";

export default class ServerVolumes extends Component {
  constructor() {
    super(...arguments);

    this.pollStatusLoop.perform();
  }

  get server() {
    return this.args.model.server;
  }

  get projectVolumes() {
    return this.args.model.projectVolumes ?? [];
  }

  get volumes() {
    return this.projectVolumes.filter((volume) => volume.attachment?.serverid === this.server.id);
  }

  get availableVolumes() {
    return this.projectVolumes.filter((volume) => !volume.attachment);
  }

  restartPolling = () => {
    this.pollStatusLoop.perform();
  };

  /* istanbul ignore next */
  pollStatusLoop = restartableTask(async () => {
    if (ENV.environment === "test") {
      return;
    }

    while (true) {
      const awaitingUpdate = this.volumes.some((volume) => !volume.isReady);

      await timeout(awaitingUpdate ? 4 * 1000 : 60 * 1000);
      await this.projectVolumes.reload();
    }
  });

  <template>
    <Box data-test-server-volumes @showFooter={{(canManage)}}>
      <:header>
        {{t "server.volumes.title"}}
      </:header>
      <:default>
        {{#if this.volumes.length}}
          <Table class="-mx-6.25 -mt-3 mb-4" @footer={{false}} @items={{this.volumes}}>
            <:header>
              <TableTh class="w-32">{{t "label.status"}}</TableTh>
              <TableTh>{{t "label.name"}}</TableTh>
              <TableTh>{{t "label.size"}}</TableTh>
              <TableTh>{{t "block-storage-volumes.wwn"}}</TableTh>
              <TableTh>{{t "label.tier"}}</TableTh>
              <TableTh><span class="sr-only">{{t "label.actions"}}</span></TableTh>
            </:header>
            <:body as |volume|>
              <TableTd data-test-server-volume-status={{volume.id}}>
                {{#if volume.isReady}}
                  <BadgeSuccess @text={{t "label.online"}} />
                {{else}}
                  <IconsLoadingSpinner class="size-6 text-gray-600" />
                {{/if}}
              </TableTd>
              <TableTd data-test-server-volume={{volume.id}}>
                <LinkTo
                  class="text-indigo-700 hover:underline"
                  data-test-server-volume-link={{volume.id}}
                  @route="block-storage.volumes.details"
                  @model={{volume.id}}
                >
                  {{volume.name}}
                </LinkTo>
              </TableTd>
              <TableTd>
                {{volume.sizeInGib}}
                GiB
              </TableTd>
              <TableTd data-test-server-volume-wwn={{volume.id}}>
                {{volume.wwn}}
              </TableTd>
              <TableTd>{{volume.tier.name}}</TableTd>
              <TableTd>
                {{#if (canManage)}}
                  <div class="flex justify-end">
                    <Dropdown
                      aria-label={{t "label.action-menu-aria-label" name=volume.name}}
                      data-test-server-volume-menu={{volume.id}}
                      @chevron={{false}}
                    >
                      <:header>
                        <IconsMoreVertical class="w-5" />
                      </:header>
                      <:default>
                        <DropdownItem
                          data-test-server-volume-detach={{volume.id}}
                          disabled={{not volume.isReady}}
                          {{on
                            "click"
                            (modal
                              "modals/confirm"
                              text=(t "server.volumes.detach.info")
                              titleText=(t "server.volumes.detach.title")
                              buttonText=(t "server.volumes.detach.button")
                              task=volume.detachTask
                            )
                          }}
                        >
                          {{t "server.volumes.detach.button"}}
                        </DropdownItem>
                      </:default>
                    </Dropdown>
                  </div>
                {{/if}}
              </TableTd>
            </:body>
          </Table>
        {{else}}
          <BoxEmptyState
            class="mt-4"
            @description={{t "server.volumes.empty-description"}}
            @title={{t "server.volumes.empty-title"}}
          />
        {{/if}}
      </:default>
      <:footer>
        {{#if (canManage)}}
          <GButtonCreate
            class="cursor-pointer"
            data-test-server-attach-volume
            {{on
              "click"
              (modal
                "modals/server-attach-volume"
                volumes=this.availableVolumes
                server=this.server
                onAttached=this.restartPolling
              )
            }}
          >
            {{t "server.volumes.attach-button"}}
          </GButtonCreate>
        {{/if}}
      </:footer>
    </Box>
  </template>
}
